import { SubAPI } from "./SubAPI";
import {Paginated} from "./Users";
import {PurchaseOrder} from "./Logistics";
import {DoorSpec, FrameSpec, Product} from "./Products";

export class Manufacturing extends SubAPI {

    listLogs(props: {
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<ManufactureLog>>("/api/manufacturing/logs", props)
    }

    getLog(props: {
        id: number;
    }) {
        return this.fetcher.get<ManufactureLogExt>("/api/manufacturing/log", props)
    }

    convertProducts(input: {
        output: {
            product: number
            qty: number;
        }
        input: {
            product: number
            qty: number;
        }[]
    }) {
        return this.fetcher.post<{}>("/api/manufacturing/convert-products", input)
    }

    removePoItem(props: {
        purchaseOrderItem: number;
    }) {
        return this.fetcher.post<{}>("/api/manufacturing/remove-po-item", props)
    }

    listWeldShopPos(props: {
        offset: number;
    }) {
        return this.fetcher.get<Paginated<WeldShopPo>>("/api/manufacturing/weld-shop-pos", props)
    }

    getWeldShopPo(props: {
        id: number;
    }) {
        return this.fetcher.get<WeldShopPo>("/api/manufacturing/weld-shop-po", props)
    }

    listWeldShopPoItems(props: {
        purchaseOrder: number;
    }) {
        return this.fetcher.get<WeldShopPoItem[]>("/api/manufacturing/weld-shop-po/items", props)
    }

    configureBuild(input: {
        purchaseOrderItem: number;
        outputProduct: number;
        inputProduct: number;
        actions: BuildAction[]
        purchaseComponents?: BuildPurchaseComponent[];
    }) {
        return this.fetcher.post("/api/manufacturing/build/configure", input)
    }

    completeBuild(input: {
        purchaseOrder: number;
        adminForceBuildWithOutputOnly: boolean;
    }) {
        return this.fetcher.post("/api/manufacturing/build/complete", input)
    }

    submitToLogistics(input: {
        purchaseOrder: number;
        items: BuildPurchaseComponentExt[]
    }) {
        return this.fetcher.post("/api/manufacturing/build/purchase-components/submit-to-logistics", input)
    }

    listPurchaseComponents(props: {
        purchaseOrder: number;
        pending: boolean;
    }) {
        return this.fetcher.get<BuildPurchaseComponentExt[]>("/api/manufacturing/build/purchase-components", props)
    }

    resetBuild(input: {
        purchaseOrderItem: number;
    }) {
        return this.fetcher.post("/api/manufacturing/build/reset", input)
    }

    suggestBuildSteps(input: {
        product: number;
        purchaseOrderItem: number;
    }) {
        return this.fetcher.post<BuildSolution[]>("/api/manufacturing/build/suggest", input)
    }

    searchBuildItems(input: {
        search: string;
        product?: number;
        purchaseOrderItem?: number;
    }) {
        return this.fetcher.get<BuildItemSearchResult[]>("/api/manufacturing/search-build-items", input)
    }

    upsertPreset(input: {
        id: number;
        bomItems: BuildLabourItem[];
        labourItems: BuildLabourItem[];
        name: string;
        archived: boolean;

        setupWithProduct?: number;
        setupFilterForPrepKey?: string;

        renderActionForPoItem?: number;
        renderActionForProduct?: number;
    }) {
        return this.fetcher.post<BuildAction>("/api/manufacturing/preset", input)
    }

    listPresets(input: {
    }) {
        return this.fetcher.get<BuildAction[]>("/api/manufacturing/presets", input)
    }
}

export type BuildPurchaseComponentExt = {
    id: number;
    qty: number;
    product: Product
    orderRequest: number | null;
    orderStatus: string | null;
    inventoryReservation: number | null;

    orderMode: "purchase" | "warehouse-first";
    inventoryId: number | null;
    inventoryAvailableQty: number;
}

export type ManufactureLog = {
    id: number;
    createdAt: string;
    createdBy: number;
    createdByName: string;
    forInventoryRequest: number;
    product: number;
    productName: string;
    poNumber: string;
    po: number;
    labourTimeHours: number;
    labourCostCents: number;
    inputProductCount: number;
    productCostCents: number;
}

export type ManufactureLogExt = ManufactureLog & {

}

export type BuildSolution = {
    type: "build" | "rework";
    actions: BuildAction[];
    estLabourHours: number;
    reworkSourceItem: {
        product: Product;
        inventory: number;
        stocking: boolean;
        kind: string;
        kind2: string;
        satisfiesToDoKeys: string[] | null
    } | null;
    reworkSourceQty: number;
    reworkDetail: ReworkDetail | null | undefined;
    errors: string[]
}

export const todoPrepPrefix = "prep:"

export type ReworkDetail = {
    updateOffsetsForCutDown: any;
    updateOffsetsForCustom: UpdateOffsets;
    updateCutDownWithNoOffsetChanges: any;
    warnCheckPreps: any;
    warnElevationMismatch: any;
}

export type UpdateOffsets = {
    items: Partial<{
        a: number;
        aStar: number;
        b: number;
        c: number;
        d: number;
        e: number;
        clf: number;
        clfStar: number;
        cld: number;
        cldStar: number;
        dbd: number;
        dbdStar: number;
        bs: number;
        dbf: number;
        dbfStar: number;
    }>
}

export type BuildItemSearchResult = {
    type: "buildItem"
    buildItem: BuildAction;
} | {
    type: "product";
    product: Product;
}

export type BuildAction = {
    id: number;
    name: string;
    labour: BuildLabourItem[];
    products: BuildProductItem[];
    reworkDetail?: ReworkDetail;
    error?: string | null;
    missing?: BuildMissingProduct[];
    satisfiesToDoKeys?: string[];
    isPresetEditable: boolean;
    productType: "door" | "frame";
    archived: boolean;
}

export type BuildPurchaseComponent = {
    qty: number;
    label: string;
    productId: number;
}

export type BuildMissingProduct = {
    label: string;
    qty: number;
    productId: number;
}

export type BuildProductItem = {
    id: number;
    label: string;
    product: number;
    inventoryItem: number | null;
    manufacturer: number;
    manufacturerName: string;
    productName: string;
    productCode: string;
    dimensions: string;
    qty: number;
    qtyAvailable: number;
    userQtyFormula: UserQtyFormula | null;
    perLengthInventoryIncrement: string | null;
    reworkDetail?: ReworkDetail;
    errors?: string[];
}

export type UserQtyFormula = {
    inputs: UserQtyFormulaInput[];
    formula: string;
}

export type UserQtyFormulaInput = {
    label: string;
    key: string;
}

export type BuildLabourItem = {
    id: number;
    label: string;
    qtyHours: number;
    totalCost: number;
    hourlyCostCents: number;
}

export type WeldShopPo = PurchaseOrder & {
    qtyDone: number;
    qtyRemaining: number;
    hasStockingInventory: boolean | null;

    componentQtyPending: number;
    componentQtyOnOrder: number;
}

export type WeldShopPoItem = {
    id: number;
    product: number;
    purchaseOrderItem: number;

    projectName?: string;
    projectLabel?: string;
    openingName?: string;
    hasStockBuildInventory: boolean | null;
    reworkDetail: null | ReworkDetail;

    door?: DoorSpec;
    frame?: FrameSpec;
    dimWidth: string;
    dimHeight: string;

    qty: number;
    qtySetup: number;
    qtyDone: number;

}
